import { useEffect, useMemo, useState } from "react";
import { Route, Routes, useParams } from "react-router-dom";
import { Loader2, LockKeyhole } from "lucide-react";
import { API, errMsg, quickClient } from "../lib/api";
import { ClientProvider } from "../context/ClientContext";
import POForm from "./POForm";
import POSaved from "./POSaved";
import axios from "axios";

const key = (token) => `mm-qt-${token}`;

const PinGate = ({ token, company, onUnlock, notice }) => {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/q/${token}/unlock`, { pin });
      onUnlock(data.quick_token);
    } catch (err) {
      setError(errMsg(err));
      setPin("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="rise mx-auto mt-10 max-w-xs space-y-5 text-center" data-testid="quick-pin-form">
      <LockKeyhole className="mx-auto text-brand" size={36} />
      <div>
        <h1 className="font-heading text-2xl font-bold">{company}</h1>
        <p className="text-sm text-muted-foreground">Enter the 4-digit PIN to raise a purchase order.</p>
      </div>
      <input className="field mono text-center tracking-[0.6em]" inputMode="numeric" pattern="\d{4}" maxLength={4} autoFocus required
        value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="quick-pin-input" />
      {notice && <p className="text-sm font-medium text-amber-600 dark:text-amber-400" data-testid="quick-pin-notice">{notice}</p>}
      {error && <p className="text-sm font-medium text-destructive" data-testid="quick-pin-error">{error}</p>}
      <button className="btn btn-primary w-full" disabled={busy || pin.length !== 4} data-testid="quick-pin-submit-btn">
        {busy && <Loader2 size={18} className="animate-spin" />} Open form
      </button>
    </form>
  );
};

export default function QuickPO() {
  const { token } = useParams();
  const [status, setStatus] = useState(null);
  const [qt, setQt] = useState(() => localStorage.getItem(key(token)));
  const [notice, setNotice] = useState("");

  useEffect(() => {
    axios.get(`${API}/q/${token}/status`).then((r) => setStatus(r.data)).catch((e) => setStatus({ active: false, error: errMsg(e) }));
  }, [token]);

  const ctx = useMemo(() => {
    const lock = () => {
      localStorage.removeItem(key(token));
      setQt(null);
      setNotice("Your session ended or the PIN was changed. Please enter the PIN again.");
    };
    const client = quickClient(token, () => localStorage.getItem(key(token)), lock);
    return { client, quick: true, base: `/q/${token}`, newPath: `/q/${token}`, registerPath: null,
      fileUrl: (p) => `${API}/files/${p}?qt=${encodeURIComponent(localStorage.getItem(key(token)) || "")}` };
  }, [token]);

  const unlock = (t) => { localStorage.setItem(key(token), t); setNotice(""); setQt(t); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b bg-background/90 backdrop-blur-lg">
        <div className="mx-auto flex h-14 max-w-3xl items-baseline gap-2 px-4 pt-4">
          <span className="font-heading text-lg font-bold tracking-tight">Man&nbsp;&amp;&nbsp;Moda</span>
          <span className="mono-label text-muted-foreground">Quick PO</span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 pb-32 pt-5">
        {!status ? <p className="text-muted-foreground">Loading…</p>
          : !status.active ? <p className="panel mt-10 p-6 text-center" data-testid="quick-link-inactive">{status.error}</p>
          : !qt ? <PinGate token={token} company={status.company_name} onUnlock={unlock} notice={notice} />
          : (
            <ClientProvider value={ctx}>
              <Routes>
                <Route index element={<POForm key="quick" />} />
                <Route path="pos/:id/saved" element={<POSaved />} />
              </Routes>
            </ClientProvider>
          )}
      </main>
    </div>
  );
}
